import * as Yup from "yup/es";
import { TestContext } from "yup/es";
import { AnyObject } from "yup/es/types";

export enum InsufficientCover {
  Gas,
  Electricity,
}

export interface SolarDiverterForm {
  familyMembers: number;
  hotWaterReq: number;
  minCWTemp: number;
  maxCWTemp: number;
  reqHWTemp: number;
  boilerEfficiency: number;
  gasCost: number;
  solarDiverterCost: number;
  installationCost: number;
  daysInsufficientSolar: number;
  electricityCost: number;
  insufficientCoverType: InsufficientCover;
  electricityExportPrice: number;
}

interface Field<K extends keyof SolarDiverterForm> {
  id: K;
  label: string;
  initialValue: SolarDiverterForm[K];
  hint?: string;
  startAdornment?: string;
  endAdornment?: string;
}

export const fields: readonly Field<keyof SolarDiverterForm>[] = [
  {
    id: "familyMembers",
    label: "Number of family members",
    initialValue: 4,
  },
  {
    id: "hotWaterReq",
    label: "Average daily hot water requirement per member",
    initialValue: 50,
    endAdornment: "L",
  },
  {
    id: "minCWTemp",
    label: "Typical minimum cold water temperature",
    initialValue: 10,
    endAdornment: "°C",
  },
  {
    id: "maxCWTemp",
    label: "Typical maximum cold water temperature",
    initialValue: 20,
    endAdornment: "°C",
  },
  {
    id: "reqHWTemp",
    label: "Hot water temperature required",
    initialValue: 60,
    endAdornment: "°C",
  },
  {
    id: "boilerEfficiency",
    label: "Boiler efficiency",
    initialValue: 80,
    endAdornment: "%",
  },
  {
    id: "gasCost",
    label: "Cost of gas per kWh",
    initialValue: 0.085,
    startAdornment: "£",
  },
  {
    id: "solarDiverterCost",
    label: "Cost of solar diverter",
    initialValue: 435,
    startAdornment: "£",
  },
  {
    id: "installationCost",
    label: "Cost of installation",
    initialValue: 100,
    startAdornment: "£",
  },
  {
    id: "daysInsufficientSolar",
    label: "Days in year with insufficient solar generation",
    initialValue: 110,
  },
  {
    id: "electricityCost",
    label: "Cost of electricity per kWh",
    initialValue: 0.075,
    startAdornment: "£",
  },
  {
    id: "insufficientCoverType",
    label: "Use gas or electricity to cover insufficient solar days",
    initialValue: InsufficientCover.Gas,
  },
  {
    id: "electricityExportPrice",
    label: "Price paid per kWh of exported electricity",
    initialValue: 0.04,
    startAdornment: "£",
  },
];

const requiredMessage = "Please complete this field";

export const getInitialValues = (): SolarDiverterForm => {
  const initialValues: Partial<SolarDiverterForm> = {};
  for (const { id, initialValue } of fields) {
    initialValues[id] = initialValue;
  }
  return initialValues as SolarDiverterForm;
};

const getWaterTempValidation = () =>
  Yup.number()
    .min(0, "Temperature must be at least 0°C")
    .max(100, "Temperature must be at most 100°C")
    .required(requiredMessage);

const getAtLeastZeroValidator = () =>
  Yup.number().min(0, "Must be greater than 0").required(requiredMessage);

export const SolarDiverterFormSchema = Yup.object().shape({
  familyMembers: Yup.number()
    .min(1, "Must have at least one family member")
    .required("Specify at least one family member"),
  hotWaterReq: getAtLeastZeroValidator(),
  minCWTemp: getWaterTempValidation(),
  maxCWTemp: getWaterTempValidation().test(
    "is-min-max-cold-correct",
    "Must be hotter than min cold water temperature",
    (value: number | undefined, ctx: TestContext<AnyObject>) => {
      return ctx.parent["minCWTemp"] < (value ?? 0);
    }
  ),
  reqHWTemp: getWaterTempValidation().test(
    "is-hot-enough",
    "Must be greater than average cold water temperature",
    (value: number | undefined, ctx: TestContext<AnyObject>) => {
      return (
        (ctx.parent["maxCWTemp"] + ctx.parent["minCWTemp"]) / 2 < (value ?? 0)
      );
    }
  ),
  boilerEfficiency: Yup.number()
    .min(0, "Efficiency must be at least 0%")
    .max(100, "Efficiency must be at most 100%")
    .required(requiredMessage),
  gasCost: getAtLeastZeroValidator(),
  solarDiverterCost: getAtLeastZeroValidator(),
  installationCost: getAtLeastZeroValidator(),
  daysInsufficientSolar: Yup.number()
    .min(0, "Must be greater than 0 days")
    .max(365, "Must be at most 365 days")
    .required(requiredMessage),
  electricityCost: getAtLeastZeroValidator(),
  insufficientCoverType: Yup.number().required(requiredMessage),
  electricityExportPrice: getAtLeastZeroValidator(),
});
